import { PrivateShowMask, PublicShowMask } from '@sda/sdk/masks';
import { Reducers } from '@sda/enums/store';
import { ProfileTypes } from '@sda/enums';
import { SDK } from './helpers/sdk';
import { SDKFactory } from './helpers/sdk.factory';
import { AffiliateMask } from './masks/affiliate.mask';
import type { Invitation } from '@sda/interfaces/invitation';
import type { Document } from '@sda/interfaces/show/document';
import type { FetchDataType, FirestoreClientData, Sitemap } from './interfaces';
import type {
  PrivateShow, PublicShow, Show, NormalizedPrivateProfile,
  NormalizedPublicProfile,
} from '@sda/interfaces/show';
import type { Message } from '@sda/interfaces/message';
import type { Order, ShowOrderMetadata } from '@sda/interfaces/order';
import type { ShowTicketMetadata, Ticket } from '@sda/interfaces/ticket';
import type { Affiliate } from '@sda/interfaces/affiliate';

/**
 * SDK Class to interact with Firestore this.
 */
export class Shows extends SDK<PublicShow> {
  constructor(apiClient: FirestoreClientData) {
    super(apiClient, Reducers.SHOWS);

    this.collection = this.fsh.Schema.PublicShows;

    this.PublicMap = PublicShowMask;

    this.PrivateMap = PrivateShowMask;
  }

  async getCombined(id: string): Promise<Show & FetchDataType> {
    return this.getCombinedRecord<PrivateShow>(
      id,
      this.Private().collection,
    );
  }

  /* Public Subcollections */

  Affiliates(id: string): SDK<Affiliate> {
    return SDKFactory(
      this.fsh.clientData,
      this.fsh.getCollection<Affiliate>(this.fsh.Schema.PublicShows, id, 'Affiliates'),
      { reducerName: Reducers.AFFILIATES, mask: AffiliateMask },
    );
  }

  Metadata(id: string): typeof result {
    const result = {
      Orders: () => SDKFactory<ShowOrderMetadata>(
        this.fsh.clientData,
        this.fsh.getCollection<ShowOrderMetadata>(this.fsh.Schema.PublicShows, id, 'Metadata'),
        { documentId: 'Orders' },
      ),
      Tickets: () => SDKFactory<ShowTicketMetadata>(
        this.fsh.clientData,
        this.fsh.getCollection<ShowTicketMetadata>(this.fsh.Schema.PublicShows, id, 'Metadata'),
        { documentId: 'Tickets' },
      ),
    };

    return result;
  }

  PublicProfiles(id: string): SDK<NormalizedPublicProfile> {
    // For brevity, this is referred to in the code as PublicProfiles but the actual collection is PublicShowProfiles
    // This makes collection group queries possible, in order to not conflict with the PublicThreadProfiles collection
    return SDKFactory(
      this.fsh.clientData,
      this.fsh.getCollection<NormalizedPublicProfile>(this.fsh.Schema.PublicShows, id, 'PublicShowProfiles'),
      {
        reducerName: (profile) => {
          if (profile.profileType === ProfileTypes.ACT) {
            return Reducers.ACTS;
          }

          if (profile.profileType === ProfileTypes.VENUE) {
            return Reducers.VENUES;
          }

          return Reducers.PROFILES;
        },
      },
    );
  }

  /* Private & Private Subcollections */

  Private(): typeof ExtendedSDK {
    interface PrivateSDK extends SDK<PrivateShow> {
      Invitations: (id: string) => SDK<Invitation>;
      Messages: (id: string) => SDK<Message>;
      Orders: (id: string) => SDK<Order>;
      PrivateDocuments: (id: string) => SDK<Document>;
      Tickets: (id: string) => SDK<Ticket>;
      PrivateProfiles: (id: string) => SDK<NormalizedPrivateProfile>;
    }

    let ExtendedSDK = SDKFactory<PrivateShow>(
      this.fsh.clientData,
      this.fsh.Schema.PrivateShows,
      { reducerName: Reducers.SHOWS },
    ) as PrivateSDK;

    ExtendedSDK.Invitations = (id: string) => SDKFactory<Invitation>(
      this.fsh.clientData,
      this.fsh.getCollection<Invitation>(this.fsh.Schema.PrivateShows, id, 'Invitations'),
      { reducerName: Reducers.INVITATIONS },
    );

    ExtendedSDK.Messages = (id: string) => SDKFactory<Message>(
      this.fsh.clientData,
      this.fsh.getCollection<Message>(this.fsh.Schema.PrivateShows, id, 'Messages'),
      { reducerName: Reducers.MESSAGES },
    );

    ExtendedSDK.Orders = (id: string) => SDKFactory<Order>(
      this.fsh.clientData,
      this.fsh.getCollection<Order>(this.fsh.Schema.PrivateShows, id, 'Orders'),
    );

    ExtendedSDK.PrivateDocuments = (id: string) => SDKFactory<Document>(
      this.fsh.clientData,
      this.fsh.getCollection<Document>(this.fsh.Schema.PrivateShows, id, 'PrivateDocuments'),
      { reducerName: Reducers.DOCUMENTS },
    );

    ExtendedSDK.PrivateProfiles = (id: string) => SDKFactory<NormalizedPrivateProfile>(
      this.fsh.clientData,
      this.fsh.getCollection<NormalizedPrivateProfile>(this.fsh.Schema.PrivateShows, id, 'PrivateShowProfiles'),
      {
        reducerName: (profile) => {
          if (profile.profileType === ProfileTypes.ACT) {
            return Reducers.ACTS;
          }

          if (profile.profileType === ProfileTypes.VENUE) {
            return Reducers.VENUES;
          }

          return Reducers.PROFILES;
        },
      },
    );

    ExtendedSDK.Tickets = (id: string) => SDKFactory<Ticket>(
      this.fsh.clientData,
      this.fsh.getCollection<Ticket>(this.fsh.Schema.PrivateShows, id, 'Tickets'),
      { reducerName: Reducers.TICKETS },
    );

    return ExtendedSDK;
  }

  Sitemap(): SDK<Sitemap> {
    return SDKFactory<Sitemap>(this.fsh.clientData, this.fsh.Schema.Sitemap_Shows);
  }
}
